import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { humanise } from "../lib/format";
import type { ContactTag } from "../lib/types";
import { HelpTip } from "./kit/HelpTip";

export function Loading({ label = "Loading" }: { label?: string }) {
  return (
    <div className="state" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <span>{label}…</span>
    </div>
  );
}

/** The danger mark: always shown with words, so red is never the only signal. */
export function DangerIcon() {
  return (
    <svg className="danger-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M8 1.5L15 14.5H1Z" />
      <path d="M8 6v4.2M8 11.8v1.2" />
    </svg>
  );
}

export function ErrorState({ error, onRetry }: { error: Error | string; onRetry?: () => void }) {
  const msg = typeof error === "string" ? error : error.message;
  return (
    <div className="state state-error" role="alert">
      <p>
        <DangerIcon />
        {msg}
      </p>
      {onRetry && (
        <button type="button" className="btn" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="state state-empty">
      <p className="state-title">{title}</p>
      {children && <div className="state-body">{children}</div>}
    </div>
  );
}

/** Loading, error and empty handled in one place. */
export function Async<T>({
  state,
  empty,
  children,
  label,
}: {
  state: { data: T | undefined; error: Error | null; loading: boolean; reload: () => void };
  empty?: (d: T) => ReactNode | null;
  children: (d: T) => ReactNode;
  label?: string;
}) {
  if (state.loading && state.data === undefined) return <Loading label={label} />;
  if (state.error) return <ErrorState error={state.error} onRetry={state.reload} />;
  if (state.data === undefined) return <Loading label={label} />;
  const e = empty?.(state.data);
  if (e) return <>{e}</>;
  return <>{children(state.data)}</>;
}

// Status tags (Ogma StatusTag): moss only for sent and booked kinds of done, amber-soft for waiting on you, muted
// for filed and closed, and the danger style (with its "!" mark) only for real failures. A loss is not an error.
const TONE: Record<string, string> = {
  approved: "ok",
  verified: "ok",
  ready: "ok",
  sent: "ok",
  published: "ok",
  held: "ok",
  won: "ok",
  done: "ok",
  credentials_added: "ok",
  booked: "booked",
  awaiting_approval: "warn",
  scheduled: "info",
  signed_up: "info",
  warming: "info",
  draft: "plain",
  not_started: "plain",
  not_needed: "plain",
  rejected: "plain",
  failed: "bad",
  paused: "bad",
  lost: "plain",
  cancelled: "plain",
  no_show: "plain",
};

const STATUS_LABEL: Record<string, string> = {
  credentials_added: "Key saved",
  not_started: "Not started",
  signed_up: "Signed up",
  verified: "Connected",
  not_needed: "Not needed",
  awaiting_approval: "Waiting for you",
  no_show: "No-show",
};

export function StatusChip({ status }: { status: string }) {
  return <span className={`chip chip-${TONE[status] ?? "plain"}`}>{STATUS_LABEL[status] ?? humanise(status)}</span>;
}

/** A modal dialog with focus moved in, Escape to close and focus returned on close. */
export function Dialog({ title, onClose, children, footer }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    const first = el?.querySelector<HTMLElement>("textarea, input, select, button:not([data-close])");
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab" && el) {
        const items = [...el.querySelectorAll<HTMLElement>("a[href], button:not([disabled]), textarea, input, select")];
        if (!items.length) return;
        const [a, z] = [items[0]!, items[items.length - 1]!];
        if (e.shiftKey && document.activeElement === a) {
          e.preventDefault();
          z.focus();
        } else if (!e.shiftKey && document.activeElement === z) {
          e.preventDefault();
          a.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      prev?.focus?.();
    };
  }, [onClose]);
  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby={id} ref={ref}>
        <div className="dialog-head">
          <h2 id={id}>{title}</h2>
          <button type="button" className="btn-icon" data-close aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="dialog-body">{children}</div>
        {footer && <div className="dialog-foot">{footer}</div>}
      </div>
    </div>
  );
}

/** Asks for a short reason, then runs the action. Used for stage moves, rejections and change requests. */
export function ReasonDialog({
  title,
  label,
  confirm,
  onConfirm,
  onClose,
  children,
  optional = false,
}: {
  title: string;
  label: string;
  confirm: string;
  onConfirm: (reason: string) => Promise<void>;
  onClose: () => void;
  children?: ReactNode;
  optional?: boolean;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = useId();
  const submit = async () => {
    if (!optional && !reason.trim()) {
      setError("Add a few words so the change is clear later.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
      onClose();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? "Saving…" : confirm}
          </button>
        </>
      }
    >
      {children}
      <label className="field" htmlFor={id}>
        <span className="field-label">{label}</span>
        <textarea id={id} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} aria-invalid={Boolean(error)} />
      </label>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}

export function KeyValues({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="kv">
      {rows.map(([k, v]) => (
        <div key={k} className="kv-row">
          <dt>{k}</dt>
          <dd>{v === null || v === undefined || v === "" ? "—" : v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A lede of this many words or fewer stays on the page under the title; anything longer opens from the (?). */
export const SHORT_LEDE_WORDS = 14;

/** A long written explanation; a lede built from parts (a firm's name and link, say) is a fact and stays on the page. */
function longLede(lede: ReactNode): lede is string {
  return typeof lede === "string" && lede.trim().split(/\s+/).length > SHORT_LEDE_WORDS;
}

/**
 * The top of a page: the title, a one-line lede, and the page's one main action on the right (`children`). A longer
 * explanation opens from a (?) beside the title (UX review, 30 Oct 2026: one line of text under a title at most).
 */
export function PageHead({ title, children, lede, code, meta }: { title: string; children?: ReactNode; lede?: ReactNode; code?: string; meta?: ReactNode }) {
  const tip = longLede(lede);
  return (
    <header className="page-head" data-step={code}>
      <div>
        {/* code: the process step behind the page; kept for links and tests, never shown to owners (plain language). */}
        <div className="page-title">
          <h1>{title}</h1>
          {tip ? <HelpTip label={`About ${title}`}>{lede}</HelpTip> : null}
        </div>
        {lede && !tip ? <p className="lede">{lede}</p> : null}
        {meta && <div className="page-meta">{meta}</div>}
      </div>
      {children && <div className="page-actions">{children}</div>}
    </header>
  );
}

/** Announces the result of an action to screen readers and shows it briefly. */
export function Notice({ text, tone = "ok" }: { text: string | null; tone?: "ok" | "bad" }) {
  if (!text) return null;
  return (
    <p className={`notice notice-${tone}`} role={tone === "bad" ? "alert" : "status"}>
      {tone === "bad" && <DangerIcon />}
      {text}
    </p>
  );
}

// ---------------------------------------------------------------- contact tags (3.10)
/** Tag definitions by key, for looking up labels and colours. */
export function tagMap(defs: ContactTag[] | undefined): Map<string, ContactTag> {
  return new Map((defs ?? []).map((d) => [d.key, d]));
}

export function tagLabel(key: string, defs?: Map<string, ContactTag>): string {
  return defs?.get(key)?.label ?? humanise(key);
}

function tagStyle(colour: string | null | undefined): CSSProperties | undefined {
  return colour && /^#[0-9a-f]{6}$/i.test(colour) ? ({ "--tag": colour } as CSSProperties) : undefined;
}

/** One tag as a coloured chip, with an optional remove button. */
export function TagChip({ tag, defs, onRemove, busy }: { tag: string; defs?: Map<string, ContactTag>; onRemove?: () => void; busy?: boolean }) {
  const label = tagLabel(tag, defs);
  return (
    <span className="tag" style={tagStyle(defs?.get(tag)?.colour)} title={defs?.get(tag)?.description ?? undefined}>
      {label}
      {onRemove && (
        <button type="button" className="tag-x" onClick={onRemove} disabled={busy} aria-label={`Remove tag ${label}`}>
          ×
        </button>
      )}
    </span>
  );
}

/** A person's tags in definition order (unknown tags last). */
export function TagList({ tags, defs, empty = null }: { tags: string[] | undefined; defs?: Map<string, ContactTag>; empty?: ReactNode }) {
  const list = [...(tags ?? [])];
  if (!list.length) return <>{empty}</>;
  const order = (k: string) => defs?.get(k)?.sort_order ?? Number.MAX_SAFE_INTEGER;
  list.sort((a, b) => order(a) - order(b) || a.localeCompare(b));
  return (
    <span className="tags">
      {list.map((t) => (
        <TagChip key={t} tag={t} defs={defs} />
      ))}
    </span>
  );
}

/** A filter chip that toggles on and off. */
export function TagToggle({ def, pressed, onToggle }: { def: ContactTag; pressed: boolean; onToggle: () => void }) {
  return (
    <button type="button" className="tag tag-toggle" style={tagStyle(def.colour)} aria-pressed={pressed} onClick={onToggle} title={def.description ?? undefined}>
      {def.label}
      <span className="tag-count" aria-label={`${def.people} people`}>
        {def.people}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------- Ogma motifs
/** A mono reference made from a record's own id (OG-1A2B), never invented. */
export function refOf(id: string | null | undefined, prefix = "OG"): string {
  const hex = String(id ?? "").replace(/[^0-9a-f]/gi, "");
  return hex ? `${prefix}-${hex.slice(0, 4).toUpperCase()}` : "";
}

/** Inline stem-line progress: one notch per step reached, the next one outlined in amber when asked for. */
export function StemProgress({ done, total, label, next = false }: { done: number; total: number; label?: string; next?: boolean }) {
  const d = Math.max(0, Math.min(done, total));
  return (
    <span className="stem-progress">
      <span className="stem" aria-hidden="true">
        {Array.from({ length: total }, (_, i) => (
          <span key={i} className={`stem-notch${i < d ? " is-done" : next && i === d ? " is-next" : ""}`} />
        ))}
      </span>
      {label && <span className="stem-label">{label}</span>}
    </span>
  );
}

/** The pipeline spine: one stem line across the stages; stage N carries N notches, seal when it has records. */
export function StemSpine({ stages }: { stages: { key: string; label: string; count: number; note?: string; href?: string; aria?: string }[] }) {
  return (
    <ol className="spine">
      {stages.map((s, i) => {
        const body = (
          <>
            <span className="spine-notches" aria-hidden="true">
              {Array.from({ length: Math.min(i + 1, 8) }, (_, k) => (
                <span key={k} className={`spine-notch${s.count ? " is-on" : ""}`} />
              ))}
            </span>
            <span className="spine-label">
              {String(i + 1).padStart(2, "0")} · {s.label}
            </span>
            <span className="spine-figure">{s.count}</span>
            {s.note && <span className="spine-note">{s.note}</span>}
          </>
        );
        return (
          <li key={s.key} className="spine-stage">
            {s.href ? (
              <Link to={s.href} className="spine-link" aria-label={s.aria}>
                {body}
              </Link>
            ) : (
              body
            )}
          </li>
        );
      })}
    </ol>
  );
}
